import React, { useState } from "react";
import { Button, Card, Col, Form, Row, Spinner, FormGroup, Alert } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import * as Yup from 'yup';
import { Formik, Field } from 'formik';
import bg1 from "../assets/img/bg1.jpg";
import { createAuthService } from '../services/authService';
import { useAuthState } from '../hooks/useAuthState';
import logo from "../assets/img/brandimg.png";

const initialValues = {
  name: '',
  email: '',
  password: '',
  confirmPassword: '',
  acceptTerms: false,
};

const validationSchema = Yup.object().shape({
  name: Yup.string().required('Name is required'),
  email: Yup.string().email('Email is invalid').required('Email is required'),
  password: Yup.string()
    .min(8, 'Password must be at least 8 characters')
    .required('Password is required'),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref('password'), null], 'Passwords must match')
    .required('Confirm Password is required'),
  acceptTerms: Yup.bool().oneOf([true], 'Accept Terms & Conditions is required'),
});

export default function Signup() {
  const authState = useAuthState();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const authService = createAuthService(authState);

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      setLoading(true);
      setError('');
      await authService.handleRegister(values);
      navigate('/auth/verify');
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
      setSubmitting(false);
    }
  };

  return (
    <div className="page-sign d-block py-0">
      <Row className="g-0">
        <Col md="7" lg="5" xl="4" className="col-wrapper">
          <Card className="card-sign">
            <Card.Header>
              <img src={logo} alt="logo" className="header-logo" style={{ width: '170px', height: '170px' }}/>
              <Card.Title>Sign Up</Card.Title>
              <Card.Text>It's free to signup and only takes a minute.</Card.Text>
            </Card.Header>
            <Card.Body>
              {error && <Alert variant="danger">{error}</Alert>}
              
              <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
                validateOnMount={true}
              >
                {({ isSubmitting, errors, touched, handleSubmit, dirty, isValid }) => (
                  <Form onSubmit={handleSubmit} className="needs-validation" noValidate>
                    <FormGroup className="mb-3">
                      <Form.Label htmlFor="name">Name</Form.Label>
                      <Field
                        id="name"
                        name="name"
                        type="text"
                        className={`form-control ${touched.name && errors.name ? 'is-invalid' : ''}`}
                        disabled={isSubmitting || loading}
                      />
                      {touched.name && errors.name && (
                        <div className="invalid-feedback d-block">
                          {errors.name}
                        </div>
                      )}
                    </FormGroup>

                    <FormGroup className="mb-3">
                      <Form.Label htmlFor="email">Email address</Form.Label>
                      <Field
                        id="email"
                        name="email"
                        type="email"
                        className={`form-control ${touched.email && errors.email ? 'is-invalid' : ''}`}
                        disabled={isSubmitting || loading}
                      />
                      {touched.email && errors.email && (
                        <div className="invalid-feedback d-block">
                          {errors.email}
                        </div>
                      )}
                    </FormGroup>

                    <FormGroup className="mb-3">
                      <Form.Label htmlFor="password">Password</Form.Label>
                      <Field
                        id="password"
                        name="password"
                        type="password"
                        className={`form-control ${touched.password && errors.password ? 'is-invalid' : ''}`}
                        disabled={isSubmitting || loading}
                      />
                      {touched.password && errors.password && (
                        <div className="invalid-feedback d-block">
                          {errors.password}
                        </div>
                      )}
                    </FormGroup>

                    <FormGroup className="mb-3">
                      <Form.Label htmlFor="confirmPassword">Confirm Password</Form.Label>
                      <Field
                        id="confirmPassword"
                        name="confirmPassword"
                        type="password"
                        className={`form-control ${touched.confirmPassword && errors.confirmPassword ? 'is-invalid' : ''}`}
                        disabled={isSubmitting || loading}
                      />
                      {touched.confirmPassword && errors.confirmPassword && (
                        <div className="invalid-feedback d-block">
                          {errors.confirmPassword}
                        </div>
                      )}
                    </FormGroup>

                    <FormGroup className="mb-4">
                      <div className="form-check">
                        <Field
                          id="acceptTerms"
                          name="acceptTerms"
                          type="checkbox"
                          className={`form-check-input ${touched.acceptTerms && errors.acceptTerms ? 'is-invalid' : ''}`}
                          disabled={isSubmitting || loading}
                        />
                        <Form.Label className="form-check-label ms-2" htmlFor="acceptTerms">
                          I agree to the <Link to="">Terms of Service</Link> and <Link to="">Privacy Policy</Link>
                        </Form.Label>
                        {touched.acceptTerms && errors.acceptTerms && (
                          <div className="invalid-feedback d-block">
                            {errors.acceptTerms}
                          </div>
                        )}
                      </div>
                    </FormGroup>

                    <Button 
                      type="submit" 
                      variant="primary" 
                      className="btn-sign w-100"
                      disabled={!dirty || !isValid || isSubmitting || loading}
                    >
                      {(isSubmitting || loading) ? (
                        <>
                          <Spinner
                            as="span"
                            animation="border"
                            size="sm"
                            role="status"
                            aria-hidden="true"
                            className="me-2"
                          />
                          Creating Account...
                        </>
                      ) : (
                        'Create Account'
                      )}
                    </Button>
                  </Form>
                )}
              </Formik>
            </Card.Body>
            <Card.Footer>
              Already have an account? <Link to="/auth/signin">Sign In</Link>
            </Card.Footer>
          </Card>
        </Col>
        <Col className="d-none d-lg-block">
          <img src={bg1} className="auth-img" alt="" style={{ 
            width: '100%',
            height: '100vh',
            objectFit: 'cover'
          }}/>
        </Col>
      </Row>
    </div>
  );
}